import { useMemo } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import meanBy from 'lodash/meanBy';

import { CloudVolumeSeries } from '@cast/types-stop';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const CLOUD_VOLUME_CURRENT_METRICS_QUERY_KEY = 'cloud-volume-current-metrics';
const getCloudVolumeCurrentMetricsQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  volumeId: string | undefined;
}) =>
  [
    CLOUD_VOLUME_CURRENT_METRICS_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
    params.volumeId,
  ] as const;

type CloudVolumeCurrentMetricsQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  volumeId: string | undefined;
  startTime: string;
  endTime: string;
  enabled?: boolean;
};

const getCloudVolumeCurrentMetricsQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  volumeId,
  startTime,
  endTime,
  enabled = true,
}: CloudVolumeCurrentMetricsQueryParams) =>
  queryOptions({
    queryKey: getCloudVolumeCurrentMetricsQueryKey({
      organizationId,
      clusterId,
      nodeName,
      volumeId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getCloudVolumeMetrics({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
        volumeId: volumeId!,
        startTime,
        endTime,
        step: '5m',
      });

      return data;
    },
    enabled: makeIsEnabled(
      !!organizationId,
      !!clusterId,
      !!nodeName,
      !!volumeId,
      enabled
    ),
  });

type UseCloudVolumeCurrentMetricsQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  volumeId?: string;
  enabled?: boolean;
};

export const useCloudVolumeCurrentMetricsQuery = ({
  clusterId,
  nodeName,
  volumeId,
  enabled,
}: UseCloudVolumeCurrentMetricsQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const endTime = dayjs().toISOString();
  const startTime = dayjs().subtract(30, 'minute').toISOString();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getCloudVolumeCurrentMetricsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      volumeId,
      startTime,
      endTime,
      enabled,
    })
  );

  const currentAvg = useMemo((): CloudVolumeSeries | undefined => {
    const series = data?.series;

    if (!series?.length) {
      return undefined;
    }

    return {
      iops: meanBy(series, (s) => s.iops || 0),
      provisionedIops: meanBy(series, (s) => s.provisionedIops || 0),
      throughputBytes: meanBy(series, (s) => s.throughputBytes || 0),
      provisionedThroughputBytes: meanBy(
        series,
        (s) => s.provisionedThroughputBytes || 0
      ),
    };
  }, [data]);

  return {
    currentAvg,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
